/**
 * Registro central de rutas del panel por tier (Starter / Pro / Business).
 * Usado por App.js, navegación y guards de plan.
 */
import { TIER_CORE, TIER_PRO, TIER_STARTER, TIER_BUSINESS } from '@/soluqr/tiers/constants';
import { filterNavItemsForPlanVisual } from '@/lib/billing/planNavVisual';

export { TIER_CORE, TIER_STARTER, TIER_PRO, TIER_BUSINESS };

/**
 * @typedef {Object} DashboardRouteSpec
 * @property {string} path Ruta absoluta (/dashboard/...)
 * @property {string} tier core | starter | pro | business
 * @property {string|null} [capability] capability requerida; null = todos los planes con dashboard
 * @property {string|null} [moduleCapability] módulo requerido (POS, KDS, SoluGo…); null = sin gate extra
 * @property {string} [segment] segmento relativo para <Route path=>
 * @property {string} [moduleKey] clave de módulo para error boundaries / lazy chunks
 */

/** Rutas compartidas (Starter + Pro + Business). */
export const CORE_DASHBOARD_ROUTES = [
  { path: '/dashboard', tier: TIER_CORE, capability: null, segment: '', moduleKey: 'dashboard' },
  { path: '/dashboard/config', tier: TIER_CORE, capability: null, segment: 'config', moduleKey: 'core' },
  { path: '/dashboard/design', tier: TIER_CORE, capability: null, segment: 'design', moduleKey: 'core' },
  { path: '/dashboard/products', tier: TIER_CORE, capability: null, segment: 'products', moduleKey: 'menu' },
  { path: '/dashboard/promos', tier: TIER_CORE, capability: 'orders', segment: 'promos', moduleKey: 'menu' },
  { path: '/dashboard/customers', tier: TIER_CORE, capability: 'orders', segment: 'customers', moduleKey: 'customers' },
  { path: '/dashboard/orders', tier: TIER_CORE, capability: 'orders', segment: 'orders', moduleKey: 'orders' },
  {
    path: '/dashboard/orders/kitchen',
    tier: TIER_CORE,
    capability: 'orders',
    moduleCapability: 'module_kds',
    segment: 'orders/kitchen',
    moduleKey: 'orders',
  },
  {
    path: '/dashboard/delivery',
    tier: TIER_CORE,
    capability: 'orders',
    moduleCapability: 'module_pos',
    segment: 'delivery',
    moduleKey: 'solugo',
  },
  { path: '/dashboard/solugo', tier: TIER_CORE, capability: 'orders', moduleCapability: 'module_solugo', segment: 'solugo', moduleKey: 'solugo' },
  {
    path: '/dashboard/own-delivery',
    tier: TIER_CORE,
    capability: 'orders',
    moduleCapability: 'module_pos',
    segment: 'own-delivery',
    moduleKey: 'solugo',
  },
  {
    path: '/dashboard/solugo/admin',
    tier: TIER_CORE,
    capability: 'orders',
    moduleCapability: 'module_solugo',
    segment: 'solugo/admin',
    moduleKey: 'solugo',
  },
  {
    path: '/dashboard/orders-live',
    tier: TIER_CORE,
    capability: 'orders',
    segment: 'orders-live',
    moduleKey: 'orders',
  },
  { path: '/dashboard/kds', tier: TIER_CORE, capability: 'orders', moduleCapability: 'module_kds', segment: 'kds', moduleKey: 'orders' },
  { path: '/dashboard/pos', tier: TIER_CORE, capability: 'orders', moduleCapability: 'module_pos', segment: 'pos', moduleKey: 'orders' },
  {
    path: '/dashboard/notifications',
    tier: TIER_CORE,
    capability: 'orders',
    segment: 'notifications',
    moduleKey: 'orders',
  },
  { path: '/dashboard/qr', tier: TIER_CORE, capability: null, segment: 'qr', moduleKey: 'core' },
  {
    path: '/dashboard/membership',
    tier: TIER_CORE,
    capability: null,
    segment: 'membership',
    moduleKey: 'core',
  },
  {
    path: '/dashboard/analytics',
    tier: TIER_CORE,
    capability: 'analytics',
    segment: 'analytics',
    moduleKey: 'analytics',
  },
  {
    path: '/dashboard/reports',
    tier: TIER_CORE,
    capability: 'orders',
    segment: 'reports',
    moduleKey: 'analytics',
  },
  { path: '/dashboard/tables', tier: TIER_CORE, capability: 'orders', moduleCapability: 'module_pos', segment: 'tables', moduleKey: 'orders' },
  { path: '/dashboard/support', tier: TIER_CORE, capability: null, segment: 'support', moduleKey: 'core' },
  {
    path: '/dashboard/assistant-training',
    tier: TIER_CORE,
    capability: 'assistant',
    segment: 'assistant-training',
    moduleKey: 'assistant',
  },
];

/** Rutas aisladas Pro — modificar sin tocar Starter. */
export const PRO_DASHBOARD_ROUTE_SPECS = [
  {
    path: '/dashboard/benefits-club',
    tier: TIER_PRO,
    capability: 'loyalty',
    segment: 'benefits-club',
    moduleKey: 'club',
  },
  { path: '/dashboard/team', tier: TIER_PRO, capability: 'team', segment: 'team', moduleKey: 'club' },
  {
    path: '/dashboard/broadcast',
    tier: TIER_PRO,
    capability: 'campaigns',
    segment: 'broadcast',
    moduleKey: 'club',
  },
];

/** Rutas Business — exclusivas del plan Business. */
export const BUSINESS_DASHBOARD_ROUTE_SPECS = [
  {
    path: '/dashboard/locations',
    tier: TIER_BUSINESS,
    capability: 'multi_store',
    segment: 'locations',
    moduleKey: 'business',
  },
];

export const DASHBOARD_ROUTE_REGISTRY = [
  ...CORE_DASHBOARD_ROUTES,
  ...PRO_DASHBOARD_ROUTE_SPECS,
  ...BUSINESS_DASHBOARD_ROUTE_SPECS,
];

/** Prefijo más largo primero → reglas de acceso (capability + módulo opcional). */
export const DASHBOARD_NAV_ACCESS_RULES = [...DASHBOARD_ROUTE_REGISTRY]
  .filter((r) => r.capability || r.moduleCapability)
  .map((r) => ({
    path: r.path,
    capability: r.capability || null,
    moduleCapability: r.moduleCapability || null,
  }))
  .sort((a, b) => b.path.length - a.path.length);

/** @deprecated use DASHBOARD_NAV_ACCESS_RULES */
export const DASHBOARD_NAV_CAPABILITY_RULES = DASHBOARD_NAV_ACCESS_RULES.filter((r) => r.capability).map(
  (r) => [r.path, r.capability],
);

export function capabilityForDashboardPath(pathname) {
  const norm = String(pathname || '').split('?')[0].replace(/\/$/, '') || '/dashboard';
  for (const rule of DASHBOARD_NAV_ACCESS_RULES) {
    if (norm === rule.path || norm.startsWith(`${rule.path}/`)) {
      return rule.capability;
    }
  }
  return null;
}

export function moduleCapabilityForDashboardPath(pathname) {
  const norm = String(pathname || '').split('?')[0].replace(/\/$/, '') || '/dashboard';
  for (const rule of DASHBOARD_NAV_ACCESS_RULES) {
    if (norm === rule.path || norm.startsWith(`${rule.path}/`)) {
      return rule.moduleCapability;
    }
  }
  return null;
}

export function moduleKeyForDashboardPath(pathname) {
  const norm = String(pathname || '').split('?')[0].replace(/\/$/, '') || '/dashboard';
  const sorted = [...DASHBOARD_ROUTE_REGISTRY].sort((a, b) => b.path.length - a.path.length);
  for (const route of sorted) {
    if (norm === route.path || norm.startsWith(`${route.path}/`)) {
      return route.moduleKey || 'core';
    }
  }
  return 'core';
}

export function canAccessDashboardPathByPlan(pathname, capabilities, _modules) {
  const capKey = capabilityForDashboardPath(pathname);
  if (!capKey) return true;
  if (!capabilities || typeof capabilities !== 'object') return false;
  return capabilities[capKey] === true;
}

export function filterNavItemsForCapabilities(items, capabilities, modules, planId) {
  const byCapability = (items || []).filter((item) => {
    if (!item?.path) return true;
    const path = String(item.path).replace(/\/$/, '');
    if (path === '/dashboard/membership') return true;
    return canAccessDashboardPathByPlan(item.path, capabilities, modules);
  });
  return filterNavItemsForPlanVisual(byCapability, planId);
}

export function routesForTier(tier) {
  return DASHBOARD_ROUTE_REGISTRY.filter((r) => r.tier === tier);
}
